import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled, alpha } from '@mui/material/styles';
import {
  InputBase,
  IconButton,
  MenuItem,
  ListItemText,
  CircularProgress,
  ClickAwayListener,
  Box,
  Popper,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { opportunityListActions } from '../../redux/opportunity/list/opportunityListState';

const Search = styled('div')(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: open ? alpha(theme.palette.common.white, 0.15) : 'transparent',
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.15),
  },
}));

const StyledInputBase = styled(InputBase)(({ theme, open }) => ({
  color: 'inherit',
  overflow: 'hidden',
  transition: theme.transitions.create(['width', 'opacity'], {
    duration: theme.transitions.duration.short,
  }),
  width: open ? '24ch' : '0px',
  opacity: open ? 1 : 0,

  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 0),
    width: '100%',
  },
}));

export default function SearchAppBar() {
  const dispatch = useDispatch();

  const [searchResults, setSearchResults] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [searchWord, setSearchWord] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const inputRef = React.useRef(null);
  const searchAreaRef = React.useRef(null);

  const dbDatas = useSelector((state) => state.opportunityListStore.globalDatas);
  const navigate = useNavigate();
  const isResultOpen = open && searchWord;

  React.useEffect(() => {
    if (dbDatas) {
      setSearchResults(dbDatas);
      setLoading(false);
    }
  }, [dbDatas]);

  const handleClickSearch = () => {
    setOpen(true);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleChangeSearchWord = (e) => {
    const { value } = e.target;
    const inputOpportunityNumber = value.trim();

    setSearchWord(value);

    if (inputOpportunityNumber.length !== 11) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    dispatch(opportunityListActions.getGlobalDatas({
      opportunityNumber: inputOpportunityNumber,
    }));
  };

  const handleClear = (e) => {
    e.preventDefault();

    setSearchWord('');
    setSearchResults([]);
    setLoading(false);

    inputRef.current?.focus();
  };

  const handleClickAway = () => {
    setOpen(false);
    setSearchWord('');
    setSearchResults([]);
    setLoading(false);
  };

  const handleClickResult = (result) => {
    navigate(`/opportunity/${result.id}`);

    setOpen(false);
    setSearchWord('');
    setSearchResults([]);
    setLoading(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <Search ref={searchAreaRef} open={open}>
          <IconButton
            color="inherit"
            onClick={handleClickSearch}
            size="large"
          >
            <SearchIcon />
          </IconButton>

          <StyledInputBase
            open={open}
            value={searchWord}
            onChange={handleChangeSearchWord}
            inputRef={inputRef}
            placeholder="案件番号で検索…"
            inputProps={{ 'aria-label': 'search' }}
          />

          {open && searchWord && (
            <IconButton
              onMouseDown={handleClear}
              edge="end"
              size="small"
              color="inherit"
              sx={{ mr: 1 }}
            >
              <ClearIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          )}
        </Search>

        <Popper
          open={Boolean(isResultOpen)}
          anchorEl={searchAreaRef.current}
          placement="bottom-start"
          sx={{ zIndex: 1300 }}
        >
          <Paper
            elevation={3}
            sx={{
              width: 360,
              mt: 1,
              maxHeight: 400,
              overflowY: 'auto',
            }}
          >
            {loading && (
              <MenuItem disabled>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                <ListItemText primary="検索中..." />
              </MenuItem>
            )}

            {!loading && searchResults.length === 0 && (
              <MenuItem disabled>
                <ListItemText primary="検索結果がありません" />
              </MenuItem>
            )}

            {!loading && searchResults.map((result) => (
              <MenuItem
                key={result.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleClickResult(result)}
              >
                <ListItemText
                  primary={result.opportunityNumber}
                  secondary={result.customerName}
                />
              </MenuItem>
            ))}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
